import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, ImageIcon, AlertCircle, LinkIcon, MessageCircle, Printer, CheckCircle2, BookOpen, Film } from "lucide-react";
import api from "@/lib/api";
import Seo from "@/components/Seo";

export default function HelpArticle() {
  const { slug } = useParams();
  const [mode, setMode] = useState("guide");
  const { data: a, isLoading, isError } = useQuery({
    queryKey: ["help-article", slug],
    queryFn: () => api.get(`/help/articles/${slug}`).then((r) => r.data),
    retry: false,
  });

  if (isLoading) return <div className="min-h-[50vh] grid place-items-center text-[#4A5D6B]">Loading guide…</div>;
  if (isError || !a) return (
    <div className="min-h-[50vh] grid place-items-center text-center px-6">
      <div><h1 className="font-serif text-3xl text-[#1A242B] mb-2">Guide not available</h1>
        <p className="text-[#4A5D6B] mb-4">This guide may require you to sign in with the right role.</p>
        <Link to="/help" className="eyw-btn !py-2 text-sm">Back to Help Center</Link></div>
    </div>
  );

  return (
    <div className="max-w-3xl mx-auto px-6 lg:px-10 py-14" data-testid="help-article">
      <Seo title={a.title} description={a.summary || `${a.title} — an Enhancing Your World help guide.`} path={`/help/${slug}`} />
      <Link to="/help" className="inline-flex items-center gap-1.5 text-[#144F75] text-sm mb-6 hover:gap-2.5 transition-all"><ArrowLeft size={15} /> Help Center</Link>
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="eyw-label mb-2">{a.category}</p>
          <h1 className="font-serif text-4xl text-[#1A242B] tracking-tight">{a.title}</h1>
          <p className="text-[#4A5D6B] text-lg mt-3">{a.summary}</p>
        </div>
        <button onClick={() => window.print()} data-testid="help-print" className="eyw-btn-ghost !py-2 !px-3 text-sm shrink-0 hidden sm:inline-flex"><Printer size={15} /> Print / PDF</button>
      </div>

      {a.updated_at && <p className="text-xs text-[#8FA89B] mt-3">Last updated {String(a.updated_at).slice(0, 10)}</p>}

      <div className="flex gap-2 mt-6" data-testid="help-mode-toggle">
        <button onClick={() => setMode("guide")} data-testid="help-mode-guide" className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm transition-colors ${mode === "guide" ? "bg-[#144F75] text-white" : "bg-white border border-[#144F75]/15 text-[#4A5D6B] hover:border-[#144F75]/40"}`}><BookOpen size={15} /> Guide</button>
        <button onClick={() => setMode("script")} data-testid="help-mode-script" className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm transition-colors ${mode === "script" ? "bg-[#144F75] text-white" : "bg-white border border-[#144F75]/15 text-[#4A5D6B] hover:border-[#144F75]/40"}`}><Film size={15} /> Video script</button>
      </div>

      {mode === "script" && (
        <div className="mt-8 space-y-8" data-testid="help-script">
          <p className="text-sm text-[#8FA89B] italic">Narration script — ready to record a training video. VO = voiceover; ON SCREEN = what to show.</p>
          {(a.topics || []).map((t, ti) => (
            <section key={ti} className="border-l-2 border-[#D98E73] pl-4">
              <h2 className="font-serif text-xl text-[#1A242B]">Scene {ti + 1}: {t.title}</h2>
              {t.purpose && <p className="text-sm text-[#4A5D6B] mt-1"><strong>VO intro:</strong> {t.purpose}</p>}
              <ol className="mt-3 space-y-2">
                {(t.steps || []).map((s, si) => (
                  <li key={si} className="text-sm text-[#1A242B]">
                    <span className="text-[#8FA89B]">[{ti + 1}.{si + 1}]</span> <strong>VO:</strong> {s.text}
                    {s.screen_grab && <div className="text-[#8FA89B] italic ml-8">ON SCREEN: {s.screen_grab}</div>}
                  </li>
                ))}
              </ol>
            </section>
          ))}
        </div>
      )}

      {mode === "guide" && (
      <div className="mt-10 space-y-12">
        {(a.topics || []).map((t, ti) => (
          <section key={ti} data-testid={`help-topic-${ti}`} className="border-t border-[#E5E0D8] pt-8 first:border-0 first:pt-0">
            <h2 className="font-serif text-2xl text-[#1A242B]">{t.title}</h2>
            {t.purpose && <p className="text-[#4A5D6B] mt-2"><span className="font-medium text-[#1A242B]">Purpose:</span> {t.purpose}</p>}
            {t.who?.length > 0 && (
              <p className="text-sm text-[#4A5D6B] mt-1"><span className="font-medium text-[#1A242B]">Who can use this:</span> {t.who.join(", ")}</p>
            )}

            <ol className="mt-6 space-y-5">
              {(t.steps || []).map((s, si) => (
                <li key={si} className="flex gap-4">
                  <span className="grid place-items-center h-7 w-7 rounded-full bg-[#144F75] text-white text-sm font-semibold shrink-0">{si + 1}</span>
                  <div className="flex-1">
                    <p className="text-[#1A242B]">{s.text}</p>
                    {s.screenshot_url ? (
                      <img src={s.screenshot_url} alt={s.screen_grab} className="mt-3 rounded-xl border border-[#E5E0D8] w-full" />
                    ) : s.screen_grab ? (
                      <div className="mt-3 rounded-xl border-2 border-dashed border-[#144F75]/25 bg-[#144F75]/[0.03] px-4 py-6 flex items-center gap-3 text-[#4A5D6B]" data-testid="help-screengrab-placeholder">
                        <ImageIcon size={20} className="text-[#144F75]/60 shrink-0" />
                        <span className="text-sm italic">SCREEN GRAB: {s.screen_grab}</span>
                      </div>
                    ) : null}
                  </div>
                </li>
              ))}
            </ol>

            {t.common_issues?.length > 0 && (
              <div className="mt-6 rounded-xl bg-[#D98E73]/8 border border-[#D98E73]/20 p-5">
                <h3 className="text-sm font-semibold text-[#1A242B] flex items-center gap-2 mb-2"><AlertCircle size={16} className="text-[#D98E73]" /> Common questions</h3>
                <ul className="space-y-1.5">
                  {t.common_issues.map((c, ci) => <li key={ci} className="text-sm text-[#4A5D6B] flex gap-2"><CheckCircle2 size={15} className="text-[#8FA89B] shrink-0 mt-0.5" /> {c}</li>)}
                </ul>
              </div>
            )}

            {t.related?.length > 0 && (
              <div className="mt-5">
                <h3 className="text-xs uppercase tracking-wider text-[#8FA89B] font-semibold mb-2 flex items-center gap-1.5"><LinkIcon size={13} /> Related guides</h3>
                <div className="flex flex-wrap gap-2">
                  {t.related.map((r) => (
                    <Link key={r} to={`/help/${r}`} data-testid={`help-related-${r}`} className="text-sm text-[#144F75] bg-white border border-[#144F75]/15 rounded-full px-3 py-1 hover:border-[#144F75]/40 transition-colors">{r.replace(/-/g, " ")}</Link>
                  ))}
                </div>
              </div>
            )}
          </section>
        ))}
      </div>
      )}

      <div className="mt-14 rounded-2xl bg-[#144F75] text-white p-8 text-center" data-testid="help-support-cta">
        <MessageCircle size={26} className="mx-auto text-[#D98E73] mb-3" />
        <h3 className="font-serif text-2xl">Still need help?</h3>
        <p className="text-white/80 mt-2 mb-5">Our team is here to support your participation.</p>
        <Link to="/contact" className="eyw-btn !bg-[#D98E73] hover:!bg-[#c67a60]">Contact Support</Link>
      </div>
    </div>
  );
}
